import { Outlet, useParams } from 'react-router-dom'

import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import useCyclesActions from 'modules/payroll_bundle/hooks/useCyclesActions'
import { useMatchOffcycleUrl } from 'modules/payroll_bundle/hooks/useMatchPayrollUrls'
import {
  useMatchOffcyclePaymentUrl,
  useMatchOffcycleResultImportUrl,
  useMatchOffcycleResultsUrl,
} from 'modules/payroll_bundle/hooks/useMatchPayrollUrls'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import NavigationTabs from 'modules/payroll_bundle/pages/CyclesPage/Header/NavigationTabs'
import { getTabs } from 'modules/payroll_bundle/pages/CyclesPage/Header/NavigationTabs/utils'
import useHasNavigation from 'modules/payroll_bundle/pages/CyclesPage/useHasNavigation'
import payments from 'modules/payroll_bundle/store/collections/payments'
import resultsCollection from 'modules/payroll_bundle/store/collections/results'

const OffCyclesPage = () => {
  const { navigation } = useNavigation()
  const { policyId: paramsPolicyId } = useParams()
  const payrollBundleContext = usePayrollBundlePageContext()
  const { selectedPolicy, policyPeriod } = payrollBundleContext
  const status = policyPeriod?.attributes.status
  const policyId = selectedPolicy?.id
  const policyPeriodId = policyPeriod?.id

  const policyIdString = String(policyId)
  const periodIdString = String(policyPeriodId)

  const { currentPeriod } = usePolicyPeriods()
  const hasNavigation = useHasNavigation(paramsPolicyId, currentPeriod?.id)
  const periodId = currentPeriod?.id
  const { canSeeCompanyPayrollResults } = usePayrollContextlessPolicies()

  const hasResults = resultsCollection.filterByPolicyPeriod(String(periodId)).length > 0
  const hasPayments = payments.currentPeriodPayments(periodId).length > 0

  const tabs = useUnsafeMemo(
    () =>
      getTabs({
        navigation,
        hasResults,
        hasPayments,
        policyId: paramsPolicyId,
        periodId,
        seeCompanyPayrollResults: canSeeCompanyPayrollResults,
        isOffCycle: true,
      }),
    [navigation, hasResults, hasPayments, paramsPolicyId, periodId, canSeeCompanyPayrollResults]
  )

  const isOffCyclePage = useMatchOffcycleUrl(policyId, policyPeriodId)

  const { primary, secondary } = useCyclesActions({
    status,
    policyId,
    periodId: policyPeriodId,
  })

  const showPageFooter = hasNavigation && isOffCyclePage

  let returnTo = null
  let navigateTo = null

  const isOffCyclePaymentsPage = useMatchOffcyclePaymentUrl(policyIdString, periodIdString)
  const isOffCyleResultsPage = useMatchOffcycleResultsUrl(policyIdString, periodIdString)
  const isOffCyleResultsImportPage = useMatchOffcycleResultImportUrl(policyIdString, periodIdString)

  if (isOffCyclePaymentsPage) {
    returnTo = navigation.payrollBundle.policy.period.offcycles.payments.fullPath({
      policyId,
      periodId: policyPeriodId,
    })

    navigateTo = navigation.payrollBundle.policy.period.offcycles.payments.generate.fullPath({
      policyId,
      periodId: policyPeriodId,
    })
  } else if (isOffCyleResultsPage || isOffCyleResultsImportPage) {
    returnTo = navigation.payrollBundle.policy.period.offcycles.results.fullPath({
      policyId,
      periodId: policyPeriodId,
    })

    navigateTo = navigation.payrollBundle.policy.period.offcycles.results.import.fullPath({
      policyId,
      periodId: policyPeriodId,
    })
  }

  return (
    <ResponsivePage.Body
      showHeader={false}
      {...(showPageFooter ? { transactionalActions: { primary, secondary } } : {})}
    >
      {hasNavigation && (
        <NavigationTabs tabs={tabs} periodId={periodId} policyId={paramsPolicyId} isOffcycle />
      )}

      <Outlet
        context={{
          returnTo,
          navigateTo,
          ...payrollBundleContext,
        }}
      />
    </ResponsivePage.Body>
  )
}

export default observer(OffCyclesPage)
